import { useRef, type PointerEvent } from "react";
import css from "./NumPad.module.css";

/**
 * Клавиатура NumPad — раскладка [1-9 / ✓ 0 ⌫] (эталон numpad.js:97-106,
 * numpad-pop.js:87). Вынесено из NumPad.tsx (лимит новых файлов ≤250 строк);
 * общий кирпич шита и NumPadPop — размеры/цвета задаёт css-модуль хоста
 * через проп styles (по умолчанию — стили шита NumPad.module.css; иконки
 * ✓/⌫ хост масштабирует правилом `.key svg { width/height }`).
 * ✓ зелёная (commit+close у хоста), ⌫ — long-press обрабатывает хост
 * через onDelDown/Up/Click. Флэш клавиши 200мс — императивно classList,
 * без state-чёрна на каждый тап (эталон NP:104).
 * dot (эталон numpad-pop.js v7 N:97): нижний ряд [✓ . 0 ⌫] из 4 клавиш
 * в гриде r4 — «.» уходит хосту через onDigit("."). Без dot — как раньше.
 */

/** CSS-классы клавиатуры — контракт для css-модуля хоста. */
export interface NumPadKeysStyles {
  keys: string;
  row: string;
  key: string;
  del: string;
  okk: string;
  fl: string;
  /** Ряд из 4 клавиш при dot (эталон v7 .npq-row.r4). */
  r4?: string;
}

interface NumPadKeysProps {
  onDigit: (d: string) => void;
  onOk: () => void;
  onDelDown: () => void;
  onDelUp: () => void;
  onDelClick: () => void;
  /** Клавиша «.» в нижнем ряду (decimals-режим NumPadPop, эталон v7). */
  dot?: boolean;
  /** Long-press цифры (480мс, решение 13.08): ввод + commit-close у хоста.
      Не передан — long-press выключен (шит/PhonePad как раньше). Точку не ловит. */
  onDigitLong?: (d: string) => void;
  /** Переопределение классов (NumPadPop); по умолчанию — стили шита. */
  styles?: NumPadKeysStyles;
}

const LONG_MS = 480;

/** Цифровая клавиша: обычный тап = onDigit; long-press = onLong (если дан). */
function DigitKey({ k, cls, fl, onDigit, onLong }: {
  k: string; cls: string; fl: string;
  onDigit: (d: string) => void; onLong?: (d: string) => void;
}) {
  const t = useRef<number | undefined>(undefined);
  const fired = useRef(false);
  return (
    <button
      type="button"
      className={cls}
      onPointerDown={(e) => {
        flash(e, fl);
        if (onLong && k !== ".") {
          fired.current = false;
          t.current = window.setTimeout(() => { fired.current = true; onLong(k); }, LONG_MS);
        }
      }}
      onPointerUp={() => window.clearTimeout(t.current)}
      onPointerLeave={() => window.clearTimeout(t.current)}
      onPointerCancel={() => window.clearTimeout(t.current)}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        if (fired.current) { fired.current = false; return; }
        onDigit(k);
      }}
    >
      {k}
    </button>
  );
}

const DIGIT_ROWS = [["1", "2", "3"], ["4", "5", "6"], ["7", "8", "9"]];

const SHEET_STYLES: NumPadKeysStyles = {
  keys: css.keys!,
  row: css.row!,
  key: css.key!,
  del: css.del!,
  okk: css.okk!,
  fl: css.fl!,
};

function flash(e: PointerEvent<HTMLButtonElement>, cls: string): void {
  if (!cls) return;
  const el = e.currentTarget;
  el.classList.add(cls);
  window.setTimeout(() => el.classList.remove(cls), 200);
}

function IcoDel() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M9 5h12v14H9l-6-7z" />
      <path d="M12.5 9.5l5 5M17.5 9.5l-5 5" />
    </svg>
  );
}

function IcoOk() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M4 12.5l6 6L20 6.5" />
    </svg>
  );
}

export function NumPadKeys({ onDigit, onOk, onDelDown, onDelUp, onDelClick, dot, onDigitLong, styles }: NumPadKeysProps) {
  const s = styles ?? SHEET_STYLES;
  const rows = [...DIGIT_ROWS, dot ? ["OK", ".", "0", "DEL"] : ["OK", "0", "DEL"]];
  return (
    <div className={s.keys}>
      {rows.map((row) => (
        <div key={row.join()} className={row.length === 4 && s.r4 ? `${s.row} ${s.r4}` : s.row}>
          {row.map((k) =>
            k === "DEL" ? (
              <button
                key={k}
                type="button"
                className={`${s.key} ${s.del}`}
                aria-label="Стереть"
                onPointerDown={(e) => {
                  flash(e, s.fl);
                  onDelDown();
                }}
                onPointerUp={onDelUp}
                onPointerLeave={onDelUp}
                onPointerCancel={onDelUp}
                onClick={onDelClick}
              >
                <IcoDel />
              </button>
            ) : k === "OK" ? (
              <button
                key={k}
                type="button"
                className={`${s.key} ${s.okk}`}
                title="Подтвердить"
                onPointerDown={(e) => flash(e, s.fl)}
                onClick={onOk}
              >
                <IcoOk />
              </button>
            ) : (
              <DigitKey key={k} k={k} cls={s.key} fl={s.fl} onDigit={onDigit} onLong={onDigitLong} />
            ),
          )}
        </div>
      ))}
    </div>
  );
}
